/**
 * Bodies do combo Operador → legacy-api.
 *
 * externalId sempre presente e único `op-…` (BUG 1 — user.js ~9074/9219).
 * Cupom vai só em addFields.numberCodCupom — não é chave de dedup.
 * Giftcard/balance: só resgate (verify_giftcard.ejs); lançamento = noop.
 * details[]: pontuação por SKU (regra-por-produto / hasProductsByAdditional).
 * Product.value no schema legado é String — parseFloat em catalogCost.
 */

import type { Passbook } from '@/shared/types';
import { isRegraIntegrado } from '@/shared/lib/passbook-utils';
import { buildOperatorAddFields } from './operator-add-fields';
import { redemptionExternalId } from './operator-external-id';
import { detectOperatorProgram } from './operator-rules';

/** Contexto mínimo pra inversão value↔price (aceita Passbook direto, por tipagem estrutural). */
export interface CatalogCostContext {
  /** redeemType=discount + essa flag: `value` vira R$ abatido, `price` deixa de ser usado. */
  usesProductValueFieldAsDiscount?: boolean;
  /** Custo em selos do resgate nesses passbooks — a cartela inteira, não o `points` do produto. */
  maxInputValidation?: number;
}

export interface CatalogProduct {
  _id: string;
  name: string;
  points?: number;
  /** Pontos (legado: String no schema Product). */
  value?: number | string;
  /** Desconto em R$ quando redemptionType === 'discount'. */
  price?: number | string;
  sku?: string;
  redemptionType?: string;
  enabled?: boolean | string;
}

export interface OperatorSaleDetail {
  sku: string;
  name: string;
  count: number;
}

export interface OperatorConfirmPayload {
  idPassbook: string;
  uniqueId: string;
  serialNumber: string;
  userReceive: string;
  purchaseValue: number;
  awardUnits: number;
  /**
   * Base da compra usada no ganho (líquido/bruto).
   * Quando `regraCartao.integrado`, a API recalcula a partir deste valor — não envie awardUnits.
   */
  earnBase?: number;
  /** Compra − descontos (Total / remanescente). Sempre usado em moneyValorCompra. */
  netValue?: number;
  redemptionEnabled: boolean;
  redemptionAmount: number;
  productRedemption?: CatalogProduct | null;
  /** Sempre `op-…` único por confirmação — nunca omitir, nunca cupom. */
  externalId: string;
  purchaseCode?: string;
  nfceAccessKey?: string;
  nfceRawUrl?: string;
  details?: OperatorSaleDetail[];
  addFieldValues?: Record<string, string>;
}

function isDiscountProduct(product: CatalogProduct): boolean {
  return (product.redemptionType ?? 'product').toLowerCase() === 'discount';
}

/** passbook.usesProductValueFieldAsDiscount + produto discount: value vira R$, não custo. */
function invertsProductValue(product: CatalogProduct, ctx?: CatalogCostContext): boolean {
  return Boolean(ctx?.usesProductValueFieldAsDiscount) && isDiscountProduct(product);
}

function toNumber(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? 0));
  return Number.isFinite(n) ? n : 0;
}

/**
 * Custo em pontos do produto do catálogo (value pode vir String).
 * usesProductValueFieldAsDiscount inverte: value é R$ abatido, então o custo do resgate
 * vira a cartela inteira (`passbook.maxInputValidation`) — não o `points` do produto.
 */
export function catalogCost(product: CatalogProduct, ctx?: CatalogCostContext): number {
  if (invertsProductValue(product, ctx)) return toNumber(ctx?.maxInputValidation);
  return toNumber(product.points ?? product.value);
}

export function catalogPrice(product: CatalogProduct): number {
  return toNumber(product.price);
}

/**
 * Desconto em R$ na conta — só para redemptionType=discount.
 * Semântica canônica do Product: `price` = R$ abatidos; `value` = custo na carteira (pts/selos).
 * Exceção: passbook.usesProductValueFieldAsDiscount inverte — `value` vira R$ abatidos, `price` some.
 */
export function catalogBillDiscount(product: CatalogProduct, ctx?: CatalogCostContext): number {
  if (!isDiscountProduct(product)) return 0;
  if (invertsProductValue(product, ctx)) return toNumber(product.value);
  return catalogPrice(product);
}

/** Valor gravado em addFields.moneyValorCompra — sempre o remanescente (Total da UI). */
export function resolveMoneyValorCompra(
  payload: OperatorConfirmPayload,
  _passbook?: Passbook,
): number {
  const remanescente = payload.netValue ?? payload.earnBase ?? payload.purchaseValue;
  return Math.max(0, Number(remanescente) || 0);
}

function payloadAddFields(
  payload: OperatorConfirmPayload,
  passbook: Passbook,
): string | undefined {
  const values: Record<string, string> = { ...(payload.addFieldValues ?? {}) };
  // Ordem final é definida em buildOperatorAddFields (cupom antes, valor por último).
  if (payload.purchaseCode && !values.numberCodCupom) {
    values.numberCodCupom = payload.purchaseCode;
  }
  const money = resolveMoneyValorCompra(payload, passbook);
  if (money > 0 && !values.moneyValorCompra) {
    values.moneyValorCompra = money.toFixed(2).replace('.', ',');
  }
  return buildOperatorAddFields({
    values,
    nfceAccessKey: payload.nfceAccessKey,
    nfceRawUrl: payload.nfceRawUrl,
  });
}

/**
 * Valor enviado em addPoints:
 * - integrado: compra (earnBase) — API aplica % / regra
 * - Operador manual: unidades já calculadas (awardUnits) — API faz passthrough
 */
export function resolveAddPointsValue(
  payload: OperatorConfirmPayload,
  passbook: Passbook,
): number {
  if (isRegraIntegrado(passbook)) {
    const base = payload.earnBase ?? payload.purchaseValue;
    return Math.max(0, Number(base) || 0);
  }
  return Math.max(0, Number(payload.awardUnits) || 0);
}

export function buildAddPointsBody(
  payload: OperatorConfirmPayload,
  user: { _id?: string; name?: string },
  passbook: Passbook,
) {
  const program = detectOperatorProgram(passbook);
  const addFields = payloadAddFields(payload, passbook);
  const details = (payload.details ?? []).filter((d) => d.count > 0 && d.sku);

  // Giftcard / saldo: legado só resgata — nunca credita pelo Operador.
  if (program === 'giftcard' || program === 'balance') {
    return { kind: 'noop' as const, body: null };
  }

  // Só-resgate / sem ganho: nunca /send nem addPoints.
  if (payload.awardUnits <= 0) {
    return { kind: 'noop' as const, body: null };
  }

  if (program === 'stamps' && !passbook.hasPoints) {
    return {
      kind: 'send' as const,
      body: {
        serialNumber: payload.serialNumber,
        action: 'input',
        id_user: user._id,
        name_validate: user.name,
        user_receive: payload.userReceive,
        count_stamps: payload.awardUnits,
        uniqueId: payload.uniqueId,
        is_supervisor: false,
        externalId: payload.externalId,
        ...(addFields ? { addFields } : {}),
      },
    };
  }

  return {
    kind: 'addPoints' as const,
    body: {
      id_user: user._id,
      id_passbook: payload.idPassbook,
      uniqueId: payload.uniqueId,
      value: resolveAddPointsValue(payload, passbook),
      name_validate: user.name,
      source: 'add_points',
      hasGiftcard: Boolean(passbook.hasGiftcard),
      externalId: payload.externalId,
      ...(details.length > 0 ? { details } : {}),
      ...(addFields ? { addFields } : {}),
    },
  };
}

export function buildRedemptionBody(
  payload: OperatorConfirmPayload,
  user: { _id?: string; name?: string },
  passbook: Passbook,
) {
  const program = detectOperatorProgram(passbook);
  const externalId = redemptionExternalId(payload.externalId);
  const addFields = payloadAddFields(payload, passbook);

  if (program === 'stamps') {
    // Catálogo (discount/product): debita `value` selos via removePoints.
    // Cartela cheia (sem produto): POST /resgate.
    if (payload.productRedemption) {
      return {
        kind: 'removePoints' as const,
        body: {
          id_user: user._id,
          id_passbook: payload.idPassbook,
          uniqueId: payload.uniqueId,
          value: catalogCost(payload.productRedemption, passbook) || payload.redemptionAmount,
          name_validate: user.name,
          source: 'remove_points',
          idproduto_resgate: payload.productRedemption._id,
          externalId,
          ...(addFields ? { addFields } : {}),
        },
      };
    }
    return {
      kind: 'resgate' as const,
      body: {
        serialNumber: payload.serialNumber,
        action: 'input',
        id_user: user._id,
        name_validate: user.name,
        user_receive: payload.userReceive,
        id_passbook: payload.idPassbook,
        externalId,
        ...(addFields ? { addFields } : {}),
      },
    };
  }

  if (payload.productRedemption) {
    return {
      kind: 'removePoints' as const,
      body: {
        id_user: user._id,
        id_passbook: payload.idPassbook,
        uniqueId: payload.uniqueId,
        value: catalogCost(payload.productRedemption) || payload.redemptionAmount,
        name_validate: user.name,
        source: 'remove_points',
        // Legado validate.js / user.js lê idproduto_resgate (não produto_resgate).
        idproduto_resgate: payload.productRedemption._id,
        externalId,
        ...(addFields ? { addFields } : {}),
      },
    };
  }

  return {
    kind: 'removePoints' as const,
    body: {
      id_user: user._id,
      id_passbook: payload.idPassbook,
      uniqueId: payload.uniqueId,
      value: payload.redemptionAmount,
      name_validate: user.name,
      source: 'remove_points',
      hasGiftcard: Boolean(passbook.hasGiftcard),
      externalId,
      ...(addFields ? { addFields } : {}),
    },
  };
}
